import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRooms, Room } from '../hooks/useRooms';

// Función para obtener miniatura de videos de YouTube, Vimeo, etc.
const getVideoThumbnail = (videoUrl: string | null): string | null => {
  if (!videoUrl) return null;

  try {
    // YouTube
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
      const match = videoUrl.match(youtubeRegex);
      if (match && match[1]) {
        return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`;
      }
    }

    // Vimeo
    if (videoUrl.includes('vimeo.com')) {
      const vimeoRegex = /vimeo\.com\/([0-9]+)/;
      const match = videoUrl.match(vimeoRegex);
      if (match && match[1]) {
        // Para Vimeo necesitaríamos una API, pero podemos usar un placeholder
        return `https://via.placeholder.com/320x180/1ab7ea/FFFFFF?text=Vimeo+${match[1]}`;
      }
    }

    // Twitch
    if (videoUrl.includes('twitch.tv')) {
      return `https://via.placeholder.com/320x180/6441a5/FFFFFF?text=Transmisión+de+Twitch`;
    }

    // Si no es ninguno de los anteriores, devolver un placeholder genérico
    return `https://via.placeholder.com/320x180/333333/FFFFFF?text=Video`;

  } catch (error) {
    console.error("Error al obtener miniatura:", error);
    return null;
  }
};

interface PublicRoomsListProps {
  className?: string;
}

const PublicRoomsList: React.FC<PublicRoomsListProps> = ({ className = '' }) => {
  const { publicRooms, loading, error, fetchPublicRooms } = useRooms();
  const navigate = useNavigate();
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // Actualizar el timestamp cuando cambia la lista de salas
  useEffect(() => {
    if (!loading) {
      setLastUpdated(new Date());
      setIsRefreshing(false);
    }
  }, [publicRooms, loading]);

  const handleRefresh = () => {
    setIsRefreshing(true);

    // Establecer un tiempo límite para la actualización
    const timeoutId = setTimeout(() => {
      console.log("Tiempo de espera agotado al refrescar salas públicas.");
      setIsRefreshing(false);
    }, 10000); // 10 segundos de tiempo límite

    // Ejecutar la actualización y limpiar el timeout cuando termine
    fetchPublicRooms()
      .catch(err => console.error("Error al refrescar salas públicas:", err))
      .finally(() => {
        clearTimeout(timeoutId);
      });
  };

  if (loading && !isRefreshing) {
    return (
      <div className={`${className} flex justify-center items-center py-8`}>
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2" style={{ borderColor: 'var(--accent-color)' }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${className} p-6 text-center`}>
        <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: 'rgb(239, 68, 68)' }}>
          <p>Error al cargar las salas públicas</p>
          <p className="text-sm mt-2">Las salas se actualizarán automáticamente cuando se resuelva el error</p>
          <button
            onClick={handleRefresh}
            className="mt-4 px-4 py-2 rounded-lg bg-red-500/20 text-red-500 hover:bg-red-500/30 transition-colors"
          >
            Intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  const joinRoom = (roomId: string) => {
    navigate(`/room/${roomId}`, { replace: true });
  };

  return (
    <div className={`${className}`}>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          {publicRooms.length === 0
            ? 'No hay salas públicas disponibles'
            : `${publicRooms.length} sala${publicRooms.length !== 1 ? 's' : ''} disponible${publicRooms.length !== 1 ? 's' : ''}`}
        </p>
        <div className="flex items-center">
          <span className="text-xs mr-2" style={{ color: 'var(--text-secondary)' }}>
            Actualizado: {lastUpdated.toLocaleTimeString()}
          </span>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="rounded-full p-1 hover:bg-gray-500/10 transition-colors flex items-center justify-center"
            style={{ color: 'var(--accent-color)' }}
          >
            <svg
              className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {publicRooms.length === 0 ? (
        <div className="p-6 text-center">
          <p style={{ color: 'var(--text-secondary)' }}>No hay salas públicas activas en este momento</p>
          <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
            Se mostrarán automáticamente cuando estén disponibles
          </p>
          <button
            onClick={handleRefresh}
            className="mt-4 px-4 py-2 rounded-lg"
            style={{ backgroundColor: 'var(--button-bg)', color: 'var(--button-text)' }}
          >
            Comprobar de nuevo
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {publicRooms.map((room) => {
            const thumbnailUrl = getVideoThumbnail(room.video_url);

            return (
              <div
                key={room.id}
                className="themed-card rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl cursor-pointer group"
                onClick={() => joinRoom(room.id)}
              >
                <div className="aspect-square relative overflow-hidden">
                  {thumbnailUrl ? (
                    <div className="absolute inset-0">
                      <img
                        src={thumbnailUrl}
                        alt={room.name}
                        className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
                        onError={(e) => {
                          // Si hay error al cargar la imagen, mostrar el icono por defecto
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      <div className="absolute inset-0 bg-black/30"></div>
                    </div>
                  ) : (
                    <div
                      className="absolute inset-0 flex items-center justify-center"
                      style={{ backgroundColor: 'var(--card-bg)' }}
                    >
                      <svg
                        className="w-12 h-12 opacity-20 group-hover:opacity-30 transition-opacity"
                        fill="none"
                        stroke="currentColor"
                        style={{ color: 'var(--accent-color)' }}
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}

                  {room.video_url && (
                    <div
                      className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full"
                    >
                      <div className="flex items-center">
                        <span className="w-2 h-2 rounded-full bg-red-500 mr-1 animate-pulse"></span>
                        En vivo
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <h3
                    className="font-medium truncate text-sm"
                    style={{ color: 'var(--text-color)' }}
                  >
                    {room.name}
                  </h3>
                  <div className="flex justify-between items-center mt-1">
                    <p
                      className="text-xs truncate"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {room.owner_name}
                    </p>
                    <div
                      className="flex items-center text-xs"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      {room.viewers}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PublicRoomsList;